/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { Button, Menu, MenuItem, TextField, Stack, Chip } from "@mui/material";
import { doc, getDocs, updateDoc, addDoc, collection, getDoc, query, where } from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import { kebabCase } from "lodash";
import "./AddTag.css";

function AddTag({ viewId, componentId, initialTags = [], onUpdate }) {
  const [tagsIds, setTagsIds] = useState(initialTags);
  const [tagsData, setTagsData] = useState([]);
  const [newChip, setNewChip] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  const addTag = async () => {
    console.log("Adding tag:", newChip);
    // Check if newChip exists in Firestore tags collection
    const tagsRef = collection(db, "tags");
    const q = query(tagsRef, where("tag", "==", newChip));
    const querySnapshot = await getDocs(q);
    let newTagId;
    if (querySnapshot.size > 0) {
      newTagId = querySnapshot.docs[0].id;
      console.log("Tag already exists in Firestore, do not create a new tag", newTagId);
    } else {
      // Add tag to Firestore
      try {
        const newTagDoc = await addDoc(collection(db, "tags"), {
          tag: newChip,
          slug: kebabCase(newChip),
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        newTagId = newTagDoc.id;
        console.log("Tag added to Firestore", newTagId);
      } catch (error) {
        console.error("Error adding tag to Firestore:", error);
      }
    }

    // Get the view content item and add the tag
    const viewRef = doc(db, "views", viewId);
    const viewDoc = await getDoc(viewRef);
    if (viewDoc.exists()) {
      // Find the contents field item that matches the componentId
      const contents = viewDoc.data().contents;
      const contentItem = contents.find((item) => item.id === componentId);
      if (contentItem) {
        if (!contentItem.tags) contentItem.tags = [];
        // Add the new tag to the content item if it doesn't already exist
        if (!contentItem.tags.includes(newTagId)) {
          // Add the new tag to the content item
          const updatedTags = contentItem.tags;
          updatedTags.push(newTagId);
          // Replace the tags field with the updated tags
          contentItem.tags = updatedTags;
          // Replace the contents field with the updated item
          const updatedContents = contents.map((item) => (item.id === componentId ? contentItem : item));
          // Update the Firestore view document with the updated contents
          try {
            await updateDoc(viewRef, { contents: updatedContents });
            console.log(`Tag added to view content item ${componentId}`);
            // Update the local state with the new tag
            setTagsIds([...tagsIds, newTagId]);
            setNewChip("");
            onUpdate();
          } catch (error) {
            console.error("Error updating view content item:", error);
          }
        } else {
          console.log("Tag already exists in content item");
        }
      } else {
        console.error(`Content item ${componentId} not found in view ${viewId}`);
      }
    } else {
      console.error("View not found:", viewId);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAddChip = async () => {
    if (newChip.trim()) {
      await addTag();
      // setNewChip("");
      handleClose();
    }
  };

  const handleDeleteChip = async (tagToDelete) => {
    // Get the view content item and remove the tag
    const viewRef = doc(db, "views", viewId);
    const viewDoc = await getDoc(viewRef);
    if (viewDoc.exists()) {
      // Find the contents field item that matches the componentId
      const contents = viewDoc.data().contents;
      const contentItem = contents.find((item) => item.id === componentId);
      if (contentItem) {
        if (contentItem.tags) {
          // Remove the tag from the content item if it exists
          const updatedTags = contentItem.tags.filter((tag) => tag !== tagToDelete.id);
          // Replace the tags field with the updated tags
          contentItem.tags = updatedTags;
          // Replace the contents field with the updated item
          const updatedContents = contents.map((item) => (item.id === componentId ? contentItem : item));
          // Update the Firestore view document with the updated contents
          try {
            await updateDoc(viewRef, { contents: updatedContents });
            console.log(`Tag ${tagToDelete.tag} removed from view content item ${componentId}`);
            // Update the local state with the new tag
            setTagsIds(tagsIds.filter((tag) => tag !== tagToDelete.id));
            onUpdate();
          } catch (error) {
            console.error("Error updating view content item when removing tag:", error);
          }
        }
      } else {
        console.error(`Content item ${componentId} not found in view ${viewId}`);
      }
    } else {
      console.error("View not found:", viewId);
    }
  };

  // Get all tags from Firestore that match document references in the tags array
  if (!loading && (initialTags.length !== tagsIds.length || tagsIds.length !== tagsData.length)) setLoading(true);
  useEffect(() => {
    const fetchTags = async () => {
      const tagsDocs = await Promise.all(
        tagsIds.map(async (tagId) => {
          const tagRef = doc(db, "tags", tagId);
          const tagDoc = await getDoc(tagRef);
          const tagData = tagDoc.data();
          tagData.id = tagDoc.id;
          return tagData;
        })
      );
      setTagsData(tagsDocs);
      setLoading(false);
    };
    if (loading) fetchTags();
  }, [tagsIds, loading]);

  return (
    <div className="component-tag-wrapper">
      <Button className="addTagBtn" onClick={handleClick} variant="contained" disableElevation={true} size="small">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#919fa9" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2.51093 11.1008C2.17228 10.7645 1.98706 10.3041 2.0007 9.82906L2.13593 4.82204C2.16207 3.90121 2.90751 3.16093 3.83365 3.13611L8.87568 3.0007C9.35522 2.98716 9.81887 3.1711 10.1575 3.50738L17.6496 10.9474C18.3314 11.6234 18.3314 12.7214 17.6496 13.3985L12.4711 18.541C11.7893 19.2181 10.6836 19.2181 10.0018 18.541L2.51093 11.1008ZM7.81537 6.77646C8.37106 7.3283 8.37106 8.22316 7.81537 8.775C7.25967 9.32683 6.35855 9.32683 5.80286 8.775C5.24716 8.22316 5.24716 7.3283 5.80286 6.77646C6.35855 6.22463 7.25967 6.22463 7.81537 6.77646ZM14.9938 3.99365C14.7097 3.71154 14.2484 3.71154 13.9643 3.99365C13.6813 4.27464 13.6813 4.7328 13.9643 5.01493L20.579 11.5825C20.9767 11.9775 20.9767 12.6173 20.579 13.0123L14.7698 18.7811C14.4857 19.0632 14.4857 19.5203 14.7698 19.8024C15.0539 20.0845 15.5141 20.0845 15.7982 19.8024L21.6074 14.0336C22.5733 13.0744 22.5733 11.5194 21.6074 10.5611L14.9938 3.99365Z"
            fill="#687b8d"
          />
        </svg>
      </Button>
      <Stack direction="row" spacing={1} wrap="wrap">
        {tagsData.map((tag) => (
          <Chip
            key={tag.id}
            sx={{ backgroundColor: "var(--Grey1-a)", color: "var(--Grey5)" }}
            size="small"
            className="editable-chips"
            label={tag.tag}
            onDelete={() => handleDeleteChip(tag)}
          />
        ))}
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        sx={{
          "& .MuiList-root": {
            backgroundColor: "#d8d8d4",
          },
        }}
      >
        <MenuItem>
          <TextField value={newChip} onChange={(e) => setNewChip(e.target.value)} label="New Tag" variant="outlined" size="small" fullWidth />
        </MenuItem>
        <MenuItem onClick={handleAddChip}>Add</MenuItem>
      </Menu>
    </div>
  );
}

export default AddTag;
